import React, { useState, useRef } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { FaEnvelope, FaWhatsapp, FaInstagram, FaArrowRight, FaClock, FaShieldAlt, FaChevronDown, FaPaperPlane, FaCheckCircle, FaTimesCircle, FaHeadset } from 'react-icons/fa';

const ContactPageSection = () => {
    const [formStatus, setFormStatus] = useState({ type: '', message: '' });
    const [loading, setLoading] = useState(false);
    const [openFaq, setOpenFaq] = useState(null);
    const [formData, setFormData] = useState({
        name: '', email: '', phone: '', service: '', budget: '', subject: '', message: ''
    });
    const formRef = useRef(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const sendEmail = async (e) => {
        e.preventDefault();
        setLoading(true);
        setFormStatus({ type: '', message: '' });

        try {
            const response = await fetch('/.netlify/functions/sendContactEmail', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });

            const data = await response.json();

            if (response.ok) {
                setFormStatus({ type: 'success', message: '🎉 Message sent successfully! We\'ll get back to you soon.' });
                setFormData({ name: '', email: '', phone: '', service: '', budget: '', subject: '', message: '' });
            } else {
                setFormStatus({ type: 'error', message: data.error || 'Failed to send. Please try again or reach out via WhatsApp.' });
            }
        } catch (error) {
            setFormStatus({ type: 'error', message: 'Failed to send. Please try again or reach out via WhatsApp.' });
        } finally {
            setLoading(false);
        }
    };

    const scrollToForm = () => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const contactMethods = [
        {
            icon: <FaEnvelope />,
            title: 'Email Us',
            description: 'Drop us a detailed message and we\'ll craft a thoughtful response.',
            action: 'info@octalbees.com',
            link: 'mailto:info@octalbees.com',
            accent: '#a04ff4'
        },
        {
            icon: <FaWhatsapp />,
            title: 'WhatsApp',
            description: 'Quick questions? Chat with us directly for instant support.',
            action: '+91 94705 06529',
            link: 'https://wa.me/919470506529',
            accent: '#25D366'
        },
        {
            icon: <FaInstagram />,
            title: 'Instagram',
            description: 'Follow our journey and DM us for creative collaborations.',
            action: '@octalbees',
            link: 'https://instagram.com/octalbees_',
            accent: '#E1306C'
        }
    ];

    const faqs = [
        {
            q: 'How quickly do you respond to inquiries?',
            a: 'We typically respond within 2-4 hours during business hours (Mon-Sat, 10AM-7PM IST). For urgent requests, reach out via WhatsApp for the fastest response.'
        },
        {
            q: 'What information should I include in my message?',
            a: 'The more detail the better! Include your project goals, timeline, budget range, and any reference examples. This helps us provide an accurate and helpful response.'
        },
        {
            q: 'Do you offer free consultations?',
            a: 'Yes! We offer a free 30-minute discovery call to understand your requirements and discuss how we can help. No strings attached.'
        },
        {
            q: 'Can you sign an NDA before discussing my project?',
            a: 'Absolutely. We take confidentiality seriously. We\'re happy to sign a mutual NDA before you share any proprietary information or business details.'
        }
    ];

    const services = [
        'Custom Software Development',
        'ERP & CRM Solutions',
        'Website Development',
        'Mobile App Development',
        'Business Process Automation',
        'UI/UX Design',
        'Digital Marketing',
        'Data Engineering & Analytics',
        'AI & Machine Learning',
        'IoT & Robotics',
        'App Store Compliance & Publishing',
        'Other'
    ];

    const budgets = [
        'Under ₹25,000',
        '₹25,000 – ₹50,000',
        '₹50,000 – ₹1,00,000',
        '₹1,00,000 – ₹3,00,000',
        '₹3,00,000+',
        'Not Sure Yet'
    ];

    return (
        <>
            <div className="cp-page">
                {/* ===== HERO SECTION ===== */}
                <section className="cp-hero">
                    <div className="cp-hero-grid"></div>
                    <div className="cp-hero-glow cp-hero-glow-1"></div>
                    <div className="cp-hero-glow cp-hero-glow-2"></div>
                    <div className="cp-hero-orb cp-hero-orb-1"></div>
                    <div className="cp-hero-orb cp-hero-orb-2"></div>
                    <div className="cp-hero-orb cp-hero-orb-3"></div>

                    <div className="container cp-hero-content">
                        <div className="row justify-content-center text-center">
                            <div className="col-lg-9">
                                <div className="cp-badge-wrap">
                                    <span className="cp-badge">
                                        <span className="cp-badge-dot"></span>
                                        LET'S CONNECT
                                    </span>
                                </div>

                                <h1 className="cp-hero-title">
                                    Have an Idea?<br />
                                    <span className="cp-hero-gradient">Let's Build It.</span>
                                </h1>

                                <p className="cp-hero-sub">
                                    Whether you need a stunning website, a powerful app, or an AI-driven solution — our team is ready to turn your vision into reality.
                                </p>

                                <div className="cp-hero-trust">
                                    <div className="cp-trust-item">
                                        <FaClock />
                                        <span>Replies within 2-4 hours</span>
                                    </div>
                                    <div className="cp-trust-divider"></div>
                                    <div className="cp-trust-item">
                                        <FaShieldAlt />
                                        <span>NDA Protected</span>
                                    </div>
                                    <div className="cp-trust-divider"></div>
                                    <div className="cp-trust-item">
                                        <FaHeadset />
                                        <span>Free Consultation</span>
                                    </div>
                                </div>

                                <div className="cp-hero-actions">
                                    <button onClick={scrollToForm} className="cp-btn-primary">
                                        Start a Conversation <FaArrowRight className="ms-2" />
                                    </button>
                                    <a href="https://wa.me/919470506529" target="_blank" rel="noopener noreferrer" className="cp-btn-whatsapp">
                                        <FaWhatsapp className="me-2" /> Quick Chat
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ===== CONTACT METHODS ===== */}
                <section className="cp-methods-section">
                    <div className="container">
                        <div className="row g-4 justify-content-center">
                            {contactMethods.map((method, index) => (
                                <div className="col-lg-4 col-md-6" key={index}>
                                    <a href={method.link} target={method.link.startsWith('mailto') ? '_self' : '_blank'} rel="noopener noreferrer" className="cp-method-card" style={{ '--card-accent': method.accent }}>
                                        <div className="cp-method-glow"></div>
                                        <div className="cp-method-icon">
                                            {method.icon}
                                        </div>
                                        <h3 className="cp-method-title">{method.title}</h3>
                                        <p className="cp-method-desc">{method.description}</p>
                                        <span className="cp-method-action">
                                            {method.action} <FaArrowRight className="ms-2" />
                                        </span>
                                    </a>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ===== MAIN FORM SECTION ===== */}
                <section className="cp-form-section" ref={formRef}>
                    <div className="cp-form-glow-left"></div>
                    <div className="cp-form-glow-right"></div>

                    <div className="container cp-form-container">
                        <div className="text-center mb-5">
                            <span className="cp-section-badge">GET A FREE QUOTE</span>
                            <h2 className="cp-section-heading">
                                Tell Us About Your <span className="cp-text-gradient">Project</span>
                            </h2>
                            <p className="cp-section-sub mx-auto">
                                Fill out the form below with as much detail as possible. The more we know, the better we can help you.
                            </p>
                        </div>

                        <div className="row justify-content-center">
                            <div className="col-lg-10">
                                <div className="cp-form-panel">
                                    <div className="cp-form-border-glow"></div>
                                    <form onSubmit={sendEmail}>
                                        <div className="row">
                                            {/* Name */}
                                            <div className="col-md-6 mb-4">
                                                <div className={`cp-field ${formData.name ? 'cp-field-active' : ''}`}>
                                                    <label className="cp-label">Full Name *</label>
                                                    <input
                                                        type="text"
                                                        name="name"
                                                        className="cp-input"
                                                        value={formData.name}
                                                        onChange={handleInputChange}
                                                        required
                                                    />
                                                </div>
                                            </div>

                                            {/* Email */}
                                            <div className="col-md-6 mb-4">
                                                <div className={`cp-field ${formData.email ? 'cp-field-active' : ''}`}>
                                                    <label className="cp-label">Email Address *</label>
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        className="cp-input"
                                                        value={formData.email}
                                                        onChange={handleInputChange}
                                                        required
                                                    />
                                                </div>
                                            </div>

                                            {/* Phone */}
                                            <div className="col-md-6 mb-4">
                                                <div className={`cp-field cp-phone-wrapper ${formData.phone ? 'cp-field-active' : ''}`}>
                                                    <label className="cp-label">Phone Number</label>
                                                    <PhoneInput
                                                        country={'in'}
                                                        value={formData.phone}
                                                        onChange={phone => setFormData(prev => ({ ...prev, phone }))}
                                                        countryCodeEditable={false}
                                                        inputProps={{
                                                            name: 'phone',
                                                            required: false,
                                                        }}
                                                        containerClass="cp-phone-container"
                                                        inputClass="cp-input cp-phone-input"
                                                        buttonClass="cp-phone-button"
                                                        dropdownClass="cp-phone-dropdown"
                                                    />
                                                </div>
                                            </div>

                                            {/* Service */}
                                            <div className="col-md-6 mb-4">
                                                <div className={`cp-field cp-select-field ${formData.service ? 'cp-field-active' : ''}`}>
                                                    <label className="cp-label">Service Interested In</label>
                                                    <select
                                                        name="service"
                                                        className="cp-input cp-select"
                                                        value={formData.service}
                                                        onChange={handleInputChange}
                                                    >
                                                        <option value="">Select a service</option>
                                                        {services.map((s, i) => (
                                                            <option key={i} value={s}>{s}</option>
                                                        ))}
                                                    </select>
                                                    <FaChevronDown className="cp-select-arrow" />
                                                </div>
                                            </div>

                                            {/* Budget */}
                                            <div className="col-md-6 mb-4">
                                                <div className={`cp-field cp-select-field ${formData.budget ? 'cp-field-active' : ''}`}>
                                                    <label className="cp-label">Budget Range</label>
                                                    <select
                                                        name="budget"
                                                        className="cp-input cp-select"
                                                        value={formData.budget}
                                                        onChange={handleInputChange}
                                                    >
                                                        <option value="">Select budget</option>
                                                        {budgets.map((b, i) => (
                                                            <option key={i} value={b}>{b}</option>
                                                        ))}
                                                    </select>
                                                    <FaChevronDown className="cp-select-arrow" />
                                                </div>
                                            </div>

                                            {/* Subject */}
                                            <div className="col-md-6 mb-4">
                                                <div className={`cp-field ${formData.subject ? 'cp-field-active' : ''}`}>
                                                    <label className="cp-label">Subject *</label>
                                                    <input
                                                        type="text"
                                                        name="subject"
                                                        className="cp-input"
                                                        value={formData.subject}
                                                        onChange={handleInputChange}
                                                        required
                                                    />
                                                </div>
                                            </div>

                                            {/* Message */}
                                            <div className="col-12 mb-4">
                                                <div className={`cp-field ${formData.message ? 'cp-field-active' : ''}`}>
                                                    <label className="cp-label">Project Details *</label>
                                                    <textarea
                                                        className="cp-input cp-textarea"
                                                        name="message"
                                                        rows="5"
                                                        value={formData.message}
                                                        onChange={handleInputChange}
                                                        required
                                                    ></textarea>
                                                </div>
                                            </div>
                                        </div>

                                        <button className="cp-submit-btn" type="submit" disabled={loading}>
                                            {loading ? (
                                                <span className="cp-spinner-wrap">
                                                    <span className="cp-spinner"></span>
                                                    Sending...
                                                </span>
                                            ) : (
                                                <>
                                                    Send Message <FaPaperPlane className="ms-2" />
                                                </>
                                            )}
                                        </button>

                                        {formStatus.message && (
                                            <div className={`cp-feedback cp-feedback-${formStatus.type}`}>
                                                {formStatus.type === 'success' ? <FaCheckCircle className="me-2" /> : <FaTimesCircle className="me-2" />}
                                                {formStatus.message}
                                            </div>
                                        )}
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ===== FAQ SECTION ===== */}
                <section className="cp-faq-section">
                    <div className="container">
                        <div className="text-center mb-4">
                            <span className="cp-section-badge">COMMON QUESTIONS</span>
                            <h2 className="cp-section-heading">
                                Before You <span className="cp-text-gradient">Reach Out</span>
                            </h2>
                        </div>

                        <div className="row justify-content-center">
                            <div className="col-lg-8">
                                {faqs.map((faq, index) => (
                                    <div
                                        className={`cp-faq-item ${openFaq === index ? 'cp-faq-open' : ''}`}
                                        key={index}
                                    >
                                        <button
                                            className="cp-faq-question"
                                            onClick={() => setOpenFaq(openFaq === index ? null : index)}
                                        >
                                            <span>{faq.q}</span>
                                            <FaChevronDown className="cp-faq-chevron" />
                                        </button>
                                        <div className="cp-faq-answer">
                                            <p>{faq.a}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            <style>{`
                /* =============================================
                   CONTACT PAGE — PREMIUM REDESIGN
                   ============================================= */

                .cp-page {
                    background-color: #05000a;
                    color: #fff;
                    overflow-x: clip;
                }

                .cp-page p {
                    text-align: center;
                }

                .cp-page .cp-faq-answer p {
                    text-align: left;
                }

                /* ─── HERO ─── */
                .cp-hero {
                    position: relative;
                    padding: 90px 0 60px;
                    overflow: hidden;
                }

                .cp-hero-grid {
                    position: absolute;
                    inset: 0;
                    background-image:
                        linear-gradient(rgba(160, 79, 244, 0.06) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(160, 79, 244, 0.06) 1px, transparent 1px);
                    background-size: 60px 60px;
                    mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
                    -webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
                    animation: gridDrift 40s linear infinite;
                }

                @keyframes gridDrift {
                    0% { background-position: 0 0; }
                    100% { background-position: 60px 60px; }
                }

                .cp-hero-glow {
                    position: absolute;
                    border-radius: 50%;
                    filter: blur(100px);
                    pointer-events: none;
                    animation: glowPulse 8s ease-in-out infinite alternate;
                }

                .cp-hero-glow-1 {
                    width: 600px;
                    height: 600px;
                    background: radial-gradient(circle, rgba(160, 79, 244, 0.2) 0%, transparent 70%);
                    top: -200px;
                    left: -100px;
                }

                .cp-hero-glow-2 {
                    width: 500px;
                    height: 500px;
                    background: radial-gradient(circle, rgba(59, 130, 246, 0.12) 0%, transparent 70%);
                    bottom: -150px;
                    right: -100px;
                    animation-delay: -4s;
                }

                @keyframes glowPulse {
                    0% { opacity: 0.5; transform: scale(0.9); }
                    100% { opacity: 1; transform: scale(1.1); }
                }

                .cp-hero-orb {
                    position: absolute;
                    border: 1px solid rgba(160, 79, 244, 0.15);
                    border-radius: 50%;
                    pointer-events: none;
                    animation: orbFloat 20s ease-in-out infinite;
                }

                .cp-hero-orb-1 {
                    width: 120px;
                    height: 120px;
                    top: 20%;
                    left: 8%;
                    border-color: rgba(160, 79, 244, 0.2);
                }

                .cp-hero-orb-2 {
                    width: 80px;
                    height: 80px;
                    bottom: 25%;
                    right: 10%;
                    border-color: rgba(59, 130, 246, 0.25);
                    animation-delay: -7s;
                }

                .cp-hero-orb-3 {
                    width: 50px;
                    height: 50px;
                    top: 35%;
                    right: 18%;
                    border-color: rgba(255, 180, 40, 0.15);
                    animation-delay: -13s;
                }

                @keyframes orbFloat {
                    0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.3; }
                    50% { transform: translateY(-25px) rotate(180deg); opacity: 0.7; }
                }

                .cp-hero-content {
                    position: relative;
                    z-index: 2;
                }

                .cp-badge-wrap {
                    margin-bottom: 18px;
                }

                .cp-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 6px 18px;
                    background: rgba(160, 79, 244, 0.08);
                    backdrop-filter: blur(12px);
                    border: 1px solid rgba(160, 79, 244, 0.25);
                    border-radius: 50px;
                    color: #d0a0ff;
                    font-size: 0.72rem;
                    font-weight: 700;
                    letter-spacing: 2px;
                    box-shadow: 0 0 25px rgba(160, 79, 244, 0.1);
                }

                .cp-badge-dot {
                    width: 7px;
                    height: 7px;
                    background: #a04ff4;
                    border-radius: 50%;
                    box-shadow: 0 0 10px #a04ff4;
                    animation: dotPulse 2s ease-in-out infinite;
                }

                @keyframes dotPulse {
                    0%, 100% { opacity: 0.5; transform: scale(1); }
                    50% { opacity: 1; transform: scale(1.5); }
                }

                .cp-hero-title {
                    font-size: 3.8rem;
                    font-weight: 900;
                    color: #fff;
                    line-height: 1.12;
                    margin-bottom: 20px;
                    letter-spacing: -1.5px;
                }

                .cp-hero-gradient {
                    background: linear-gradient(135deg, #ffffff 0%, #e0e0e0 20%, #a04ff4 55%, #3b82f6 100%);
                    background-size: 200% auto;
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    animation: shineGradient 5s linear infinite;
                }

                @keyframes shineGradient {
                    to { background-position: 200% center; }
                }

                .cp-hero-sub {
                    font-size: 1.05rem;
                    color: #a0a0a0;
                    max-width: 620px;
                    margin: 0 auto 28px;
                    line-height: 1.7;
                    font-weight: 300;
                    letter-spacing: 0.01em;
                }

                /* Trust Indicators */
                .cp-hero-trust {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 16px;
                    margin-bottom: 30px;
                    flex-wrap: wrap;
                }

                .cp-trust-item {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 0.82rem;
                    color: #b0b0b0;
                    font-weight: 500;
                    letter-spacing: 0.01em;
                }

                .cp-trust-item svg {
                    color: #a04ff4;
                    font-size: 0.82rem;
                }

                .cp-trust-divider {
                    width: 1px;
                    height: 14px;
                    background: rgba(255, 255, 255, 0.12);
                }

                /* Hero Buttons */
                .cp-hero-actions {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 16px;
                    flex-wrap: wrap;
                }

                .cp-btn-primary {
                    display: inline-flex;
                    align-items: center;
                    padding: 13px 30px;
                    background: #fff;
                    color: #05000a;
                    font-weight: 700;
                    font-size: 0.95rem;
                    border: none;
                    border-radius: 50px;
                    cursor: pointer;
                    text-decoration: none;
                    transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                    box-shadow: 0 10px 25px rgba(255, 255, 255, 0.1);
                    letter-spacing: 0.01em;
                }

                .cp-btn-primary:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 16px 35px rgba(255, 255, 255, 0.18);
                }

                .cp-btn-whatsapp {
                    display: inline-flex;
                    align-items: center;
                    padding: 13px 28px;
                    background: rgba(37, 211, 102, 0.08);
                    border: 1px solid rgba(37, 211, 102, 0.3);
                    color: #25D366;
                    font-weight: 600;
                    font-size: 0.95rem;
                    border-radius: 50px;
                    text-decoration: none;
                    transition: all 0.3s ease;
                }

                .cp-btn-whatsapp:hover {
                    background: rgba(37, 211, 102, 0.15);
                    border-color: rgba(37, 211, 102, 0.5);
                    color: #25D366;
                    transform: translateY(-4px);
                }

                /* ─── CONTACT METHODS ─── */
                .cp-methods-section {
                    padding: 10px 0 60px;
                    position: relative;
                }

                .cp-method-card {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                    padding: 32px 26px;
                    background: rgba(255, 255, 255, 0.02);
                    border: 1px solid rgba(255, 255, 255, 0.06);
                    border-radius: 20px;
                    text-decoration: none;
                    color: #fff;
                    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                    position: relative;
                    overflow: hidden;
                    height: 100%;
                }

                .cp-method-glow {
                    position: absolute;
                    top: -50%;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 200px;
                    height: 200px;
                    background: radial-gradient(circle, var(--card-accent, #a04ff4) 0%, transparent 70%);
                    opacity: 0;
                    filter: blur(60px);
                    transition: opacity 0.4s ease;
                    pointer-events: none;
                }

                .cp-method-card:hover {
                    transform: translateY(-10px);
                    border-color: var(--card-accent, rgba(160, 79, 244, 0.4));
                    background: rgba(255, 255, 255, 0.04);
                    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4);
                    color: #fff;
                }

                .cp-method-card:hover .cp-method-glow {
                    opacity: 0.12;
                }

                .cp-method-icon {
                    width: 58px;
                    height: 58px;
                    border-radius: 16px;
                    background: linear-gradient(135deg, var(--card-accent, #a04ff4), color-mix(in srgb, var(--card-accent, #a04ff4) 60%, black));
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.5rem;
                    color: #fff;
                    margin-bottom: 18px;
                    box-shadow: 0 10px 25px color-mix(in srgb, var(--card-accent, #a04ff4) 30%, transparent);
                    transition: transform 0.3s ease;
                }

                .cp-method-card:hover .cp-method-icon {
                    transform: scale(1.1) rotate(-5deg);
                }

                .cp-method-title {
                    font-size: 1.15rem;
                    font-weight: 700;
                    margin-bottom: 8px;
                    letter-spacing: -0.01em;
                }

                .cp-method-desc {
                    font-size: 0.88rem;
                    color: #8a8a8a;
                    line-height: 1.55;
                    margin-bottom: 16px;
                    flex-grow: 1;
                }

                .cp-method-action {
                    display: inline-flex;
                    align-items: center;
                    font-weight: 600;
                    font-size: 0.88rem;
                    color: var(--card-accent, #a04ff4);
                    transition: all 0.3s ease;
                }

                .cp-method-card:hover .cp-method-action {
                    transform: translateX(5px);
                }

                /* ─── FORM SECTION ─── */
                .cp-form-section {
                    padding: 70px 0;
                    position: relative;
                    overflow: hidden;
                }

                .cp-form-glow-left {
                    position: absolute;
                    width: 400px;
                    height: 400px;
                    background: radial-gradient(circle, rgba(160, 79, 244, 0.08) 0%, transparent 70%);
                    border-radius: 50%;
                    top: 10%;
                    left: -5%;
                    pointer-events: none;
                }

                .cp-form-glow-right {
                    position: absolute;
                    width: 350px;
                    height: 350px;
                    background: radial-gradient(circle, rgba(59, 130, 246, 0.06) 0%, transparent 70%);
                    border-radius: 50%;
                    bottom: 10%;
                    right: -5%;
                    pointer-events: none;
                }

                .cp-form-container {
                    position: relative;
                    z-index: 2;
                }

                /* Section Headers */
                .cp-section-badge {
                    display: inline-block;
                    font-size: 0.7rem;
                    font-weight: 700;
                    letter-spacing: 2.5px;
                    color: #ffb428;
                    background: rgba(255, 180, 40, 0.08);
                    padding: 6px 18px;
                    border-radius: 50px;
                    border: 1px solid rgba(255, 180, 40, 0.15);
                    margin-bottom: 16px;
                }

                .cp-section-heading {
                    font-size: 2.5rem;
                    font-weight: 800;
                    color: #fff;
                    letter-spacing: -0.5px;
                    margin-bottom: 0.8rem;
                    line-height: 1.2;
                }

                .cp-text-gradient {
                    background: linear-gradient(135deg, #a04ff4, #3b82f6);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }

                .cp-section-sub {
                    font-size: 0.98rem;
                    color: #8a8a8a;
                    max-width: 520px;
                    line-height: 1.6;
                }

                /* Form Panel */
                .cp-form-panel {
                    position: relative;
                    background: rgba(255, 255, 255, 0.02);
                    border: 1px solid rgba(255, 255, 255, 0.06);
                    border-radius: 24px;
                    padding: 40px;
                    backdrop-filter: blur(10px);
                    -webkit-backdrop-filter: blur(10px);
                }

                .cp-form-border-glow {
                    position: absolute;
                    inset: -1px;
                    border-radius: 28px;
                    background: linear-gradient(135deg, rgba(160, 79, 244, 0.15), transparent, rgba(59, 130, 246, 0.1));
                    z-index: -1;
                    opacity: 0;
                    transition: opacity 0.5s ease;
                }

                .cp-form-panel:hover .cp-form-border-glow {
                    opacity: 1;
                }

                /* Form Fields */
                .cp-field {
                    position: relative;
                }

                .cp-label {
                    display: block;
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: #8a8a8a;
                    margin-bottom: 6px;
                    letter-spacing: 0.4px;
                    text-transform: uppercase;
                    transition: color 0.3s ease;
                }

                .cp-field-active .cp-label,
                .cp-field:focus-within .cp-label {
                    color: #a04ff4;
                }

                .cp-input {
                    width: 100%;
                    background: rgba(255, 255, 255, 0.03);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 10px;
                    padding: 12px 14px;
                    color: #fff;
                    font-size: 0.92rem;
                    font-family: inherit;
                    transition: all 0.3s ease;
                    outline: none;
                }

                .cp-input::placeholder {
                    color: #555;
                }

                .cp-input:focus {
                    background: rgba(255, 255, 255, 0.05);
                    border-color: #a04ff4;
                    box-shadow: 0 0 0 3px rgba(160, 79, 244, 0.12), 0 4px 20px rgba(160, 79, 244, 0.08);
                }

                .cp-textarea {
                    resize: vertical;
                    min-height: 110px;
                }

                /* Select */
                .cp-select-field {
                    position: relative;
                }

                .cp-select {
                    appearance: none;
                    -webkit-appearance: none;
                    cursor: pointer;
                    padding-right: 40px;
                }

                .cp-select option {
                    background: #1a0f2e;
                    color: #fff;
                    padding: 10px;
                }

                .cp-select-arrow {
                    position: absolute;
                    right: 14px;
                    bottom: 16px;
                    color: #666;
                    font-size: 0.7rem;
                    pointer-events: none;
                    transition: color 0.3s ease;
                }

                .cp-field:focus-within .cp-select-arrow {
                    color: #a04ff4;
                }

                /* Submit Button */
                .cp-submit-btn {
                    width: 100%;
                    padding: 15px;
                    background: linear-gradient(135deg, #a04ff4, #7c3aed);
                    color: #fff;
                    font-size: 1rem;
                    font-weight: 700;
                    border: none;
                    border-radius: 12px;
                    cursor: pointer;
                    position: relative;
                    overflow: hidden;
                    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                    box-shadow: 0 8px 25px rgba(160, 79, 244, 0.25);
                    letter-spacing: 0.01em;
                }

                .cp-submit-btn::before {
                    content: '';
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(135deg, #7c3aed, #a04ff4);
                    opacity: 0;
                    transition: opacity 0.3s ease;
                }

                .cp-submit-btn:hover:not(:disabled) {
                    transform: translateY(-3px);
                    box-shadow: 0 14px 35px rgba(160, 79, 244, 0.35);
                }

                .cp-submit-btn:hover:not(:disabled)::before {
                    opacity: 1;
                }

                .cp-submit-btn > * {
                    position: relative;
                    z-index: 1;
                }

                .cp-submit-btn:disabled {
                    background: #333;
                    cursor: not-allowed;
                    box-shadow: none;
                }

                /* Spinner */
                .cp-spinner-wrap {
                    display: inline-flex;
                    align-items: center;
                    gap: 10px;
                }

                .cp-spinner {
                    width: 20px;
                    height: 20px;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-top-color: #fff;
                    border-radius: 50%;
                    animation: spin 0.8s linear infinite;
                }

                @keyframes spin {
                    to { transform: rotate(360deg); }
                }

                /* Feedback */
                .cp-feedback {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-top: 16px;
                    padding: 14px 18px;
                    border-radius: 10px;
                    font-weight: 600;
                    font-size: 0.88rem;
                    animation: feedbackSlide 0.4s ease;
                }

                @keyframes feedbackSlide {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .cp-feedback-success {
                    background: rgba(22, 163, 74, 0.1);
                    border: 1px solid rgba(22, 163, 74, 0.2);
                    color: #4ade80;
                }

                .cp-feedback-error {
                    background: rgba(239, 68, 68, 0.1);
                    border: 1px solid rgba(239, 68, 68, 0.2);
                    color: #f87171;
                }

                /* ─── FAQ SECTION ─── */
                .cp-faq-section {
                    padding: 50px 0 90px;
                }

                .cp-faq-item {
                    border: 1px solid rgba(255, 255, 255, 0.06);
                    border-radius: 14px;
                    margin-bottom: 10px;
                    overflow: hidden;
                    background: rgba(255, 255, 255, 0.02);
                    transition: all 0.3s ease;
                }

                .cp-faq-item:hover {
                    border-color: rgba(160, 79, 244, 0.2);
                }

                .cp-faq-open {
                    border-color: rgba(160, 79, 244, 0.3);
                    background: rgba(160, 79, 244, 0.04);
                }

                .cp-faq-question {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 18px 22px;
                    background: none;
                    border: none;
                    color: #fff;
                    font-size: 0.95rem;
                    font-weight: 600;
                    cursor: pointer;
                    text-align: left;
                    transition: color 0.3s ease;
                    gap: 14px;
                    letter-spacing: -0.01em;
                }

                .cp-faq-question:hover {
                    color: #d0a0ff;
                }

                .cp-faq-chevron {
                    font-size: 0.65rem;
                    color: #666;
                    transition: all 0.3s ease;
                    flex-shrink: 0;
                }

                .cp-faq-open .cp-faq-chevron {
                    transform: rotate(180deg);
                    color: #a04ff4;
                }

                .cp-faq-answer {
                    max-height: 0;
                    overflow: hidden;
                    transition: max-height 0.35s ease, padding 0.35s ease;
                    padding: 0 22px;
                }

                .cp-faq-open .cp-faq-answer {
                    max-height: 300px;
                    padding: 0 22px 18px;
                }

                .cp-faq-answer p {
                    color: #8a8a8a;
                    font-size: 0.88rem;
                    line-height: 1.6;
                    margin: 0;
                }

                /* ─── PHONE INPUT OVERRIDES ─── */
                .cp-phone-wrapper .react-tel-input .cp-phone-input {
                    width: 100%;
                    height: 52px;
                    background: rgba(255, 255, 255, 0.03);
                    border: 1px solid rgba(255, 255, 255, 0.06);
                    border-radius: 12px;
                    color: #fff;
                    font-size: 0.92rem;
                    padding-left: 55px;
                    transition: all 0.3s ease;
                }

                .cp-phone-wrapper .react-tel-input .cp-phone-input:focus {
                    background: rgba(255, 255, 255, 0.05);
                    border-color: #a04ff4;
                    box-shadow: 0 0 0 4px rgba(160, 79, 244, 0.1);
                }

                .cp-phone-wrapper .react-tel-input .cp-phone-button {
                    background: transparent !important;
                    border: none !important;
                    padding: 0 10px;
                    border-radius: 12px 0 0 12px;
                }
                
                .cp-phone-wrapper .react-tel-input .cp-phone-button:hover,
                .cp-phone-wrapper .react-tel-input .cp-phone-button .selected-flag:hover,
                .cp-phone-wrapper .react-tel-input .cp-phone-button .selected-flag:focus {
                    background: transparent !important;
                }

                .cp-phone-wrapper .react-tel-input .cp-phone-dropdown {
                    background: #110728;
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 12px;
                    color: #fff;
                    padding: 8px;
                    width: 300px;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
                }

                .cp-phone-wrapper .react-tel-input .cp-phone-dropdown .search {
                    background: #110728;
                    padding: 8px;
                }

                .cp-phone-wrapper .react-tel-input .cp-phone-dropdown .search-box {
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    color: #fff;
                    border-radius: 6px;
                }

                .cp-phone-wrapper .react-tel-input .cp-phone-dropdown li {
                    border-radius: 8px;
                    margin-bottom: 2px;
                    transition: background 0.2s ease;
                }

                .cp-phone-wrapper .react-tel-input .cp-phone-dropdown li:hover,
                .cp-phone-wrapper .react-tel-input .cp-phone-dropdown li.highlight,
                .cp-phone-wrapper .react-tel-input .cp-phone-dropdown li.active {
                    background: rgba(160, 79, 244, 0.2) !important;
                }

                .cp-phone-wrapper .react-tel-input .cp-phone-dropdown li .dial-code {
                    color: #a04ff4 !important;
                }

                .cp-phone-wrapper .react-tel-input .cp-phone-dropdown li .country-name {
                    color: #ddd !important;
                }
                
                .cp-phone-wrapper .react-tel-input .cp-phone-dropdown li:hover .country-name,
                .cp-phone-wrapper .react-tel-input .cp-phone-dropdown li.highlight .country-name,
                .cp-phone-wrapper .react-tel-input .cp-phone-dropdown li.active .country-name {
                    color: #fff !important;
                }

                /* ─── RESPONSIVE ─── */
                @media (max-width: 991px) {
                    .cp-hero-title { font-size: 3rem; }
                    .cp-section-heading { font-size: 2.2rem; }
                    .cp-form-panel { padding: 32px 26px; }
                    .cp-trust-divider { display: none; }
                    .cp-hero-trust { gap: 12px; }
                }

                @media (max-width: 768px) {
                    .cp-hero { padding: 70px 0 45px; }
                    .cp-hero-title { font-size: 2.4rem; letter-spacing: -0.8px; }
                    .cp-hero-sub { font-size: 0.95rem; }
                    .cp-section-heading { font-size: 1.8rem; }
                    .cp-form-panel { padding: 26px 18px; }
                    .cp-form-section { padding: 50px 0; }
                    .cp-faq-section { padding: 40px 0 60px; }
                    .cp-methods-section { padding: 5px 0 40px; }
                    .cp-hero-trust { flex-direction: column; gap: 8px; }
                    .cp-hero-actions { flex-direction: column; width: 100%; }
                    .cp-btn-primary, .cp-btn-whatsapp { width: 100%; justify-content: center; }
                    .cp-faq-question { font-size: 0.88rem; padding: 15px 18px; }
                    .cp-faq-answer { padding-left: 18px; padding-right: 18px; }
                    .cp-faq-open .cp-faq-answer { padding-bottom: 15px; }
                }

                @media (max-width: 480px) {
                    .cp-hero-title { font-size: 2rem; }
                    .cp-method-card { padding: 26px 20px; }
                }
            `}</style>
        </>
    );
};

export default ContactPageSection;